<script setup lang="ts">
import { ChevronsDown, Pause, Play } from "lucide-vue-next"
import { Motion } from "motion-v"
import type { HTMLAttributes } from "vue"
import { computed, onMounted, ref } from "vue"
import { ReservationDialogV2 } from "~/components/reservation"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { useLocation } from "~/composables/locations"
import { scrollToAndFocus } from "~/composables/scroll"
import { cn } from "~/lib/utils"

function handleScrollToAbout(event: Event) {
    event.preventDefault()
    scrollToAndFocus("about")
}

const { class: cl } = defineProps<{ 
    class?: HTMLAttributes["class"]
}>()

const { location } = useLocation()

const prefersReducedMotion = ref(false)
const videoRef = ref<HTMLVideoElement | null>(null)
const isPaused = ref(false)

onMounted(() => {
    document.documentElement.style.setProperty(
        "--hero-height", 
        `${window.innerHeight}px`
    )
    prefersReducedMotion.value = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const video = videoRef.value
    if (!video) return

    const handleFirstTouch = () => {
        if (video.paused) {
            video.play().catch(() => {
                console.warn("Autoplay blocked by browser")
            })
        }
        document.removeEventListener("touchstart", handleFirstTouch)
    }

    document.addEventListener("touchstart", handleFirstTouch, { once: true })
})

const togglePlay = () => {
    if (videoRef.value) {
        if (videoRef.value.paused) {
            videoRef.value.play()
            isPaused.value = false
        } else {
            videoRef.value.pause()
            isPaused.value = true
        }
    }
}

const heroVideo = computed(() => location.value?.pageContent?.hero?.video)
const heroImage = computed(() => location.value?.pageContent?.hero?.image)
const wordmark = computed(() => location.value?.pageContent?.hero?.wordmark)
</script>

<template>
    <section
        v-if="location"
        id="hero"
        :class="cn('relative flex flex-col items-center justify-center overflow-hidden', cl)"
        style="height: var(--hero-height, 100svh); min-height: var(--hero-height, 100svh);"
    >
        <div class="absolute inset-0 overflow-hidden">
            <video
                v-if="heroVideo && !prefersReducedMotion"
                ref="videoRef"
                playsinline
                autoplay
                muted
                loop
                class="w-full h-full object-cover brightness-75"
                :poster="heroImage || ''"
                aria-hidden="true"
                aria-label="Background video with no audio, decorative purpose only"
            >
                <source
                    :src="heroVideo"
                    type="video/mp4"
                >
                Your browser does not support the video tag.
            </video>
            <ThemeImg
                v-else
                :src="heroImage || ''"
                :alt="location?.pageContent?.hero?.alt || ''"
                class="w-full h-full object-cover brightness-75"
                loading="eager"
            />
        </div>
        <div class="absolute inset-0 hero-gradient" />
        <div class="z-20 flex flex-col items-center justify-around w-full mt-auto h-3/4 md:px-6 md:pb-16">
            <div class="w-hero-section-wordmark-container-width-small md:w-hero-section-wordmark-container-width-medium max-w-full px-4">
                <div class="flex flex-col items-center justify-center">
                    <img
                        :src="wordmark || ''"
                        class="w-full max-w-sm h-auto"
                        :alt="location?.name + ' wordmark'"
                        width="384"
                        height="128"
                        fetchpriority="high"
                        decoding="async"
                    >
                </div>
            </div>
            <div class="pb-16 md:hidden">
                <Motion
                    as="div"
                    class="flex justify-center"
                    :variants="{
                        hidden: { y: 25, opacity: 0 },
                        visible: { y: 0, opacity: 1 }
                    }"
                    initial="hidden"
                    while-in-view="visible"
                    :in-view-options="{ once: true }"
                    :transition="{
                        type: 'spring',
                        duration: 1.5
                    }"
                >
                    <ReservationDialogV2>
                        <ThemeButton
                            size="lg"
                            tabindex="0"
                            :class="cn(
                                'px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors'
                            )"
                        >
                            <span>Reservations</span>
                        </ThemeButton>
                    </ReservationDialogV2>
                </Motion>
            </div>
        </div>
        <ThemeButton
            v-if="heroVideo"
            id="play-pause-button"
            class="absolute left-4 bottom-4 z-40 bg-black/25 p-3 cursor-pointer rounded-none hover:bg-black/50 transition"
            :aria-label="isPaused ? 'Play video' : 'Pause video'"
            variant="ghost"
            tabindex="0"
            @click="togglePlay"
        >
            <Pause
                v-if="!isPaused"
                class="text-cream"
                aria-hidden="true"
                :size="16"
            />
            <Play
                v-else
                class="text-cream"
                aria-hidden="true"
                :size="16"
            />
        </ThemeButton>
        <Motion
            as="div"
            class="absolute bottom-4 hidden md:flex w-full justify-center z-20"
            :variants="{
                hidden: { opacity: 0 },
                visible: { opacity: 1 }
            }"
            initial="hidden"
            while-in-view="visible"
            :in-view-options="{ once: true }"
            :transition="{
                type: 'spring',
                delay: 1.5,
                duration: 1.5
            }"
        >
            <a
                href="#about"
                tabindex="0"
                class="flex animate-pulse rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                aria-label="Scroll to about section"
                @click="handleScrollToAbout"
                @keydown.enter="handleScrollToAbout"
                @keydown.space.prevent="handleScrollToAbout"
            >
                <ChevronsDown
                    class="size-7"
                />
            </a>
        </Motion>
    </section>
</template>
